import i18n from '@/i18n';
import { displayShopFact } from '@/i18n/catalogDisplayText';
import { fixedUi } from '@/i18n/fixedUi';
import React, { useCallback, useEffect, useMemo, useRef, useState } from 'react';
import { Link } from 'react-router-dom';
import { IPE_WORKBENCH_PATH } from '@/config/ipeRoutes';
import { useTemuShop } from '@/temu-platform/context/TemuShopContext';
import { useTemuSyncJobs } from '@/temu-platform/context/TemuSyncJobContext';
import { temuPlatformAPI } from '@/temu-platform/api/temuPlatformApi';
import {
  buildStoreBasicRows,
  getStoreDisplayName,
  getStoreStatusMeta,
  getStoreSupplierId,
  pickStoreInfoNode,
} from '@/temu-platform/utils/temuStoreInfo';
import ShopSiteListPanel from '@/temu-platform/modules/shop/ShopSiteListPanel';
import ShopBrandIpPanel from '@/temu-platform/modules/shop/ShopBrandIpPanel';
import ShopCategoryPanel from '@/temu-platform/modules/shop/ShopCategoryPanel';
import { formatSiteListSyncedAt } from '@/temu-platform/utils/temuSiteList';
import { logTemuSyncPlatformDump } from '@/temu-platform/utils/temuPlatformJsonConsole';
import TemuRefreshButton from '@/temu-platform/components/TemuRefreshButton';
import TemuSyncProgressSlot from '@/temu-platform/components/TemuSyncProgressSlot';
import TemuShopAvatar from '@/temu-platform/components/TemuShopAvatar';
import { SyncHintMark } from '@/temu-platform/components/TemuOpsListToolbar';
import { useGuardedRefresh } from '@/temu-platform/hooks/useGuardedRefresh';
import { useTemuManualSyncQuota } from '@/temu-platform/hooks/useTemuManualSyncQuota';
import PlatformShopDimensionTabs from '@/components/platform/PlatformShopDimensionTabs';
import PlatformShopPickModal from '@/components/platform/PlatformShopPickModal';
import {
  PLATFORM_ALL_SHOPS_ID,
  isAllShopsScope,
  isConcreteShopId,
} from '@/utils/platformShopScope';
import { showToast } from '@/utils/toast';
import { imageStorageAPI } from '@/utils/api';
import { pickUrlFromImageStorageResponse } from '@/utils/imageUrlUtils';
import { authLabel } from '@/pages/ipe/workbench/utils/platformShopManageUtils';
import '@/styles/temu-platform/temu-product-ops.css';
import '@/styles/temu-platform/temu-ops-list-toolbar.css';

const SHOP_PROFILE_SYNC_KINDS = ['shopProfile'];
const AVATAR_MAX_BYTES = 5 * 1024 * 1024;

const SYNC_SECTION_LABELS = {
  mallInfo: '基本信息',
  accessTokenInfo: '授权信息',
  accessTokenInfoGlobal: '授权信息(Global)',
  mallId: '店铺ID',
  brands: '品牌',
  categories: '可发类目',
};

/** 将平台 7000020 等 token 错误转成可操作提示 */
function formatTemuShopSyncError(raw) {
  const msg = String(raw || '').trim();
  if (!msg) return '';
  const lower = msg.toLowerCase();
  if (lower.includes('7000007') || lower.includes('access_token is expired') || (lower.includes('access_token') && lower.includes('expired'))) {
    return 'access_token 已过期（7000007）。请到 TEMU 卖家中心重新授权，并在平台管理更新店铺 Token；「授权过期时间」可作为是否过期的依据。';
  }
  if (lower.includes('7000020') || lower.includes('access_token invalid')) {
    return 'access_token 无效（7000020）。常见原因：token 填错、已失效/被撤销、或未用卖家中心重新授权。请到 TEMU 卖家中心重新授权并更新店铺密钥后再同步。';
  }
  if (lower.includes('7000019') || lower.includes("don't have this api access") || lower.includes('authorize this api')) {
    return '当前 access_token 未授权该接口（7000019）。请到 TEMU 卖家中心勾选对应 API 权限并重新授权。';
  }
  if (lower.includes('7000016') || lower.includes('type not exists')) {
    return '平台返回 7000016（type not exists）：接口 type 传值错误或不在本区，不是权限问题（权限不足是 7000019）。请按 PA 迁移清单核对 type 与网关。';
  }
  return msg;
}

function formatTemuBrandSyncError(raw) {
  const msg = String(raw || '').trim();
  if (!msg) return '';
  const lower = msg.toLowerCase();
  if (lower.includes('7000020') || lower.includes('access_token invalid')) {
    return formatTemuShopSyncError(msg);
  }
  if (lower.includes('7000019') || lower.includes("don't have this api access") || lower.includes('authorize this api')) {
    return '当前 access_token 未授权品牌查询（货品API组 bg.goods.brand.get；回退 PA bg.glo.goods.brand.get 亦失败）。请到 TEMU 卖家中心开通「货品API组」权限并重新授权后再同步。';
  }
  if (lower.includes('mallid unavailable')) {
    return '缺少店铺数字 ID（商家 ID），无法调用品牌接口；请确认授权或重新同步店铺信息补全商家 ID 后再试。';
  }
  return msg;
}

function KvTable({ rows }) {
  if (!rows.length) {
    return <p className="tpo-return-detail__empty">{fixedUi("暂无数据")}</p>;
  }
  return (
    <table className="tpo-return-detail__kv">
      <tbody>
        {rows.map((row) => (
          <tr key={row.key || row.label}>
            <th scope="row">{displayShopFact(row.label)}</th>
            <td>{displayShopFact(row.value)}</td>
          </tr>
        ))}
      </tbody>
    </table>
  );
}

export default function ShopInfoPage() {
  const {
    activeShop,
    loading,
    activeShopId,
    scopedShops = [],
    isAllShopsMode,
    patchLocalShop,
  } = useTemuShop();
  const { startJob, onTrackDone, isBusy } = useTemuSyncJobs();
  const [pageShopScope, setPageShopScope] = useState(() => activeShopId || '');
  const [shopPickOpen, setShopPickOpen] = useState(false);
  const [storeInfo, setStoreInfo] = useState(null);
  const [fetching, setFetching] = useState(false);
  const [uploadingAvatar, setUploadingAvatar] = useState(false);
  const avatarInputRef = useRef(null);
  const [fetchError, setFetchError] = useState('');
  const [brands, setBrands] = useState(null);
  const [brandIpLoading, setBrandIpLoading] = useState(false);
  const [brandIpError, setBrandIpError] = useState('');
  const [siteList, setSiteList] = useState(null);
  const [siteListLoading, setSiteListLoading] = useState(false);
  const [siteListError, setSiteListError] = useState('');
  const [categories, setCategories] = useState(null);
  const [categoryLoading, setCategoryLoading] = useState(false);
  const [categoryError, setCategoryError] = useState('');
  const [lastRefreshedAt, setLastRefreshedAt] = useState('');
  const prevShopIdRef = useRef(null);
  const pendingSyncRef = useRef(false);

  useEffect(() => {
    setPageShopScope(activeShopId || '');
  }, [activeShopId]);

  const showOverview = isAllShopsScope(pageShopScope)
    || (isAllShopsMode && !isConcreteShopId(pageShopScope));

  const viewShopId = useMemo(() => {
    if (isConcreteShopId(pageShopScope)) return String(pageShopScope);
    if (isConcreteShopId(activeShopId)) return String(activeShopId);
    return '';
  }, [pageShopScope, activeShopId]);

  const viewShop = useMemo(() => {
    if (!viewShopId) return null;
    return (scopedShops || []).find((s) => String(s.id) === String(viewShopId))
      || (activeShop && String(activeShop.id) === String(viewShopId) ? activeShop : null);
  }, [viewShopId, scopedShops, activeShop]);

  const loadBrandIp = useCallback(async () => {
    if (!viewShopId) {
      setBrands(null);
      setBrandIpError('');
      return null;
    }
    setBrandIpLoading(true);
    setBrandIpError('');
    try {
      const brandRes = await temuPlatformAPI.getShopBrands(viewShopId);
      setBrands(brandRes?.brands ?? null);
      return brandRes;
    } catch (e) {
      setBrands(null);
      setBrandIpError(e?.message || '读取品牌缓存失败');
      return null;
    } finally {
      setBrandIpLoading(false);
    }
  }, [viewShopId]);

  const loadCategories = useCallback(async () => {
    if (!viewShopId) {
      setCategories(null);
      setCategoryError('');
      return null;
    }
    setCategoryLoading(true);
    setCategoryError('');
    try {
      const res = await temuPlatformAPI.getShopCategories(viewShopId);
      setCategories(res?.categories ?? null);
      return res;
    } catch (e) {
      setCategories(null);
      setCategoryError(e?.message || '读取商品分类缓存失败');
      return null;
    } finally {
      setCategoryLoading(false);
    }
  }, [viewShopId]);

  const loadSiteList = useCallback(async () => {
    if (!viewShopId) {
      setSiteList(null);
      setSiteListError('');
      return null;
    }
    setSiteListLoading(true);
    setSiteListError('');
    try {
      const res = await temuPlatformAPI.getShopSiteList(viewShopId);
      setSiteList(res?.siteList ?? null);
      return res;
    } catch (e) {
      setSiteList(null);
      setSiteListError(e?.message || '读取售卖站点缓存失败');
      return null;
    } finally {
      setSiteListLoading(false);
    }
  }, [viewShopId]);

  const loadStoreInfo = useCallback(async () => {
    if (!viewShopId) {
      setStoreInfo(null);
      return null;
    }
    setFetching(true);
    setFetchError('');
    try {
      const res = await temuPlatformAPI.getShopStoreInfo(viewShopId);
      setStoreInfo(res?.storeInfo ?? null);
      return res;
    } catch (e) {
      setStoreInfo(null);
      setFetchError(e?.message || '读取店铺信息缓存失败');
      return null;
    } finally {
      setFetching(false);
    }
  }, [viewShopId]);

  const loadFromCache = useCallback(async () => {
    if (!viewShopId) {
      setLastRefreshedAt('');
      return;
    }
    const [storeRes, brandRes, siteRes] = await Promise.all([
      loadStoreInfo(),
      loadBrandIp(),
      loadSiteList(),
      loadCategories(),
    ]);
    const syncedAt = storeRes?.syncedAt
      || brandRes?.syncedAt
      || siteRes?.syncedAt
      || '';
    setLastRefreshedAt(syncedAt);
    // 打开店铺信息页时把最近一次同步的平台原始 JSON 打到控制台
    logTemuSyncPlatformDump(viewShopId, 'shop-profile', {
      get label() { return fixedUi("店铺信息"); },
      reason: '打开店铺信息页',
    });
  }, [viewShopId, loadStoreInfo, loadBrandIp, loadSiteList, loadCategories]);

  const syncFromTemuAction = useCallback(async () => {
    if (!viewShopId) {
      setLastRefreshedAt('');
      return;
    }
    setFetchError('');
    setBrandIpError('');
    setSiteListError('');
    setCategoryError('');
    try {
      const track = await startJob({
        kind: 'shopProfile',
        shopId: viewShopId,
        get label() { return fixedUi("同步店铺信息"); },
        silent: true,
      });
      if (!track?.id) return;
      const result = await new Promise((resolve) => {
        const unsub = onTrackDone(track.id, (r) => {
          unsub();
          resolve(r);
        });
      });
      await loadFromCache();
      const errors = result?.view?.errors && typeof result.view.errors === 'object'
        ? result.view.errors
        : {};
      setFetchError(formatTemuShopSyncError(
        errors.mallInfo || errors.accessTokenInfo || errors.accessTokenInfoGlobal || ''
      ));
      setBrandIpError(formatTemuBrandSyncError(errors.brands));
      setCategoryError(formatTemuShopSyncError(errors.categories || ''));
      const failedKeys = Object.keys(errors).filter((k) => errors[k]);
      const viewError = String(result?.view?.error || result?.view?.errorMessage || '').trim();
      if (result?.ok && result?.view?.ok !== false && failedKeys.length === 0 && !viewError) {
        showToast(result?.view?.message || fixedUi("店铺信息同步成功"), 'success');
      } else if (failedKeys.length > 0) {
        const firstMsg = formatTemuShopSyncError(errors[failedKeys[0]]);
        const labels = failedKeys.map((k) => SYNC_SECTION_LABELS[k] || k).join('、');
        showToast(
          failedKeys.length === 1
            ? firstMsg
            : `部分同步失败（${labels}）：${firstMsg}`,
          'warning',
        );
      } else if (!result?.ok && !result?.cancelled) {
        showToast(result?.view?.error || result?.view?.message || fixedUi("同步失败"), 'error');
      }
    } catch (e) {
      try {
        await loadFromCache();
      } catch (_) {
        /* ignore */
      }
      if (e?.message) showToast(e.message, 'error');
    }
  }, [viewShopId, loadFromCache, startJob, onTrackDone]);

  const {
    refresh: handleRefreshAll,
    refreshing: syncingLocal,
    cooldownSeconds: refreshAllCooldown,
  } = useGuardedRefresh(syncFromTemuAction);

  const quota = useTemuManualSyncQuota('shop-profile');
  const handleRefreshClick = () => {
    if (!quota.assertCanSync()) return;
    handleRefreshAll();
  };

  const handleAvatarPick = () => {
    if (uploadingAvatar || !viewShopId) return;
    avatarInputRef.current?.click();
  };

  const handleAvatarChange = async (ev) => {
    const file = ev.target.files?.[0];
    ev.target.value = '';
    if (!file || !viewShopId) return;
    if (!file.type.startsWith('image/')) {
      showToast(fixedUi("请选择图片文件"), 'warning');
      return;
    }
    if (file.size > AVATAR_MAX_BYTES) {
      showToast(fixedUi("头像图片不能超过 5MB"), 'warning');
      return;
    }
    setUploadingAvatar(true);
    try {
      const uploaded = await imageStorageAPI.save(file, { folder: 'image-space', registerImageSpace: false });
      const url = pickUrlFromImageStorageResponse(uploaded);
      if (!url) throw new Error('上传失败');
      await temuPlatformAPI.patchShopAvatar(viewShopId, url);
      patchLocalShop?.(viewShopId, { avatarUrl: url });
      showToast(fixedUi("店铺头像已更新"), 'success');
    } catch (err) {
      showToast(err?.message || '头像上传失败', 'error');
    } finally {
      setUploadingAvatar(false);
    }
  };

  useEffect(() => {
    if (showOverview || !viewShopId) {
      prevShopIdRef.current = null;
      if (!viewShopId) setLastRefreshedAt('');
      return;
    }

    const prevShopId = prevShopIdRef.current;
    prevShopIdRef.current = viewShopId;
    const shouldSync = pendingSyncRef.current;
    pendingSyncRef.current = false;

    if (shouldSync) {
      syncFromTemuAction();
      return;
    }

    if (prevShopId == null || String(prevShopId) !== String(viewShopId)) {
      loadFromCache();
    }
  }, [showOverview, viewShopId, loadFromCache, syncFromTemuAction]);

  const syncBusy = isBusy('shopProfile', viewShopId);
  const isRefreshing = syncingLocal || syncBusy;

  const shopFallbackName = viewShop?.shopName || '';
  const displayName = useMemo(
    () => getStoreDisplayName(storeInfo, shopFallbackName),
    [storeInfo, shopFallbackName],
  );
  const supplierId = useMemo(() => getStoreSupplierId(storeInfo), [storeInfo]);
  const statusMeta = useMemo(() => getStoreStatusMeta(storeInfo), [storeInfo]);
  const basicRows = useMemo(() => buildStoreBasicRows(storeInfo), [storeInfo]);
  const hasBasicInfo = Boolean(pickStoreInfoNode(storeInfo));

  const syncedMetaText = lastRefreshedAt
    ? `${fixedUi('最近同步')}：${formatSiteListSyncedAt(lastRefreshedAt)}`
    : (isRefreshing ? fixedUi('正在同步…') : fixedUi('尚未同步'));

  // 仅门禁选「全部」进入时展示；单店登录锁定当前店
  const shopDimensionTabs = isAllShopsMode && scopedShops.length >= 2 ? (
    <PlatformShopDimensionTabs
      shops={scopedShops}
      value={pageShopScope || PLATFORM_ALL_SHOPS_ID}
      onChange={setPageShopScope}
      showAll
    />
  ) : null;

  if (loading) {
    return (
      <div className="tpo-return-detail tpo-shop-info" data-testid="temu-shop-info">
        <div className="tpo-return-detail__loading">{fixedUi("加载店铺信息…")}</div>
      </div>
    );
  }

  if (showOverview) {
    return (
      <div className="tpo-return-detail tpo-shop-info" data-testid="temu-shop-info">
        <header className="tpo-return-detail__header">
          <div className="tpo-return-detail__title-block">
            <h1 className="tpo-return-detail__title">{fixedUi("店铺信息")}</h1>

          </div>
          <div className="tpo-return-detail__actions">
            <TemuRefreshButton
              onClick={() => setShopPickOpen(true)}
              size="sm"
              className="tpo-btn tpo-btn--primary tpo-btn--sm"
            >
              {fixedUi("同步店铺信息")}
            </TemuRefreshButton>
          </div>
        </header>
        {shopDimensionTabs}
        <div className="tpo-shop-info__overview">
          <div className="tpo-shop-info__table-wrap">
            <table className="tpo-shop-info__table tpo-shop-info__overview-table">
              <thead>
                <tr>
                  <th scope="col">{fixedUi("店铺名称")}</th>
                  <th scope="col">{fixedUi("店铺 ID")}</th>
                  <th scope="col">{fixedUi("授权状态")}</th>
                  <th scope="col">{fixedUi("操作")}</th>
                </tr>
              </thead>
              <tbody>
                {(scopedShops || []).map((shop) => (
                  <tr key={shop.id}>
                    <td>{shop.shopName || `店铺 ${shop.id}`}</td>
                    <td>{shop.id}</td>
                    <td>{authLabel(shop.authStatus, shop.mockBinding)}</td>
                    <td>
                      <button
                        type="button"
                        className="tpo-btn tpo-btn--ghost tpo-btn--sm"
                        onClick={() => setPageShopScope(String(shop.id))}
                      >
                        查看详情
                      </button>
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </div>
        <PlatformShopPickModal
          open={shopPickOpen}
          shops={scopedShops}
          title={fixedUi("选择要同步的店铺")}
          description={fixedUi("全部视图下请选择一家店铺后再同步店铺信息。")}
          confirmText="查看并同步"
          onClose={() => setShopPickOpen(false)}
          onConfirm={(shopId) => {
            setShopPickOpen(false);
            pendingSyncRef.current = true;
            setPageShopScope(String(shopId));
          }}
        />
      </div>
    );
  }

  if (!viewShop) {
    return (
      <div className="tpo-return-detail tpo-shop-info" data-testid="temu-shop-info">
        <header className="tpo-return-detail__header">
          <div className="tpo-return-detail__title-block">
            <h1 className="tpo-return-detail__title">{fixedUi("店铺信息")}</h1>
          </div>
        </header>
        {shopDimensionTabs}
        <div className="tpo-return-detail__loading">
          {fixedUi("尚未配置 TEMU 店铺。")}
          {' '}
          <Link to={`${IPE_WORKBENCH_PATH}?activeNav=platformShops`} className="tpo-return-detail__crumb-link">
            {fixedUi("去平台管理 · 店铺管理")}
          </Link>
        </div>
      </div>
    );
  }

  return (
    <>
      <TemuSyncProgressSlot kinds={SHOP_PROFILE_SYNC_KINDS} />
      <div className="tpo-return-detail tpo-shop-info" data-testid="temu-shop-info">
        {shopDimensionTabs}
        <header className="tpo-return-detail__header">
          <div className="tpo-shop-info__identity">
            <button
              type="button"
              className="tpo-shop-info__avatar-btn"
              onClick={handleAvatarPick}
              disabled={uploadingAvatar}
              aria-label={fixedUi("更换店铺头像")}
              title={fixedUi("点击上传自定义头像")}
            >
              <TemuShopAvatar
                className="tpo-shop-info__avatar"
                avatarUrl={viewShop?.avatarUrl}
                size={72}
              />
              <span className="tpo-shop-info__avatar-overlay" aria-hidden="true">
                {uploadingAvatar ? fixedUi("上传中") : fixedUi("更换")}
              </span>
            </button>
            <input
              ref={avatarInputRef}
              type="file"
              accept="image/*"
              className="tpo-shop-info__avatar-input"
              onChange={handleAvatarChange}
            />
            <div className="tpo-return-detail__title-block">
              <h1 className="tpo-return-detail__title">{fixedUi("店铺信息")}</h1>
              <div className="tpo-return-detail__meta">
                <strong className="tpo-return-detail__no">{displayName}</strong>
                {supplierId ? (
                  <span className="tpo-shop-info__meta-id">{fixedUi("商家 ID")} {supplierId}</span>
                ) : null}
                {hasBasicInfo || statusMeta.label !== '—' ? (
                  <span className={`tpo-records__status tpo-records__status--${statusMeta.modifier}`}>
                    {displayShopFact(statusMeta.label)}
                  </span>
                ) : null}
              </div>
              <p className="tpo-shop-info__avatar-tip">
                {fixedUi("点击头像可自定义；未设置时顶栏默认显示 TEMU")}
              </p>
            </div>
          </div>
          <div className="tpo-return-detail__actions">
            <div className="tpo-ops-list-toolbar__sync">
              <span className="tpo-sync-updated-at" title={syncedMetaText}>
                {syncedMetaText}
              </span>
              {quota.remaining != null ? (
                <span
                  className="tpo-sync-quota"
                  title={i18n.t('tiktokList.quotaTitle', { ns: 'workbench', limit: quota.limit })}
                >
                  {i18n.t('tiktokList.quotaRemain', { ns: 'workbench', remaining: quota.remaining, limit: quota.limit })}
                </span>
              ) : null}
              <span className="tpo-ops-list-toolbar__sync-action tpo-ops-list-toolbar__sync-action--with-hint">
                <SyncHintMark text="从 TEMU 后台拉取店铺基本信息、售卖站点、品牌与可发类目，更新到 IPE 本地。" />
                <TemuRefreshButton
                  onClick={handleRefreshClick}
                  loading={isRefreshing}
                  cooldownSeconds={refreshAllCooldown}
                  busyLabel={fixedUi("同步中…")}
                  size="sm"
                  className="tpo-btn tpo-btn--secondary tpo-btn--sm tpo-ops-list-toolbar__sync-btn tpo-ops-list-toolbar__sync-btn--with-hint"
                  disabled={quota.exhausted}
                >
                  {quota.exhausted ? i18n.t('tiktokList.quotaExhausted', { ns: 'workbench' }) : fixedUi('拉取 TEMU 后台数据')}
                </TemuRefreshButton>
              </span>
            </div>
          </div>
        </header>

        {fetchError ? (
          <div className="tpo-ops-error" role="alert">{fetchError}</div>
        ) : null}

        {fetching && !storeInfo && !syncBusy ? (
          <div className="tpo-return-detail__loading">{fixedUi("正在查询店铺信息…")}</div>
        ) : null}

        <div className="tpo-return-detail__body">
          <section className="tpo-return-detail__section">
            <h2 className="tpo-return-detail__section-title">{fixedUi("基本信息")}</h2>
            {!fetching && !fetchError && !hasBasicInfo ? (
              <p className="tpo-return-detail__empty">{fixedUi("暂无店铺基础信息，请点击「拉取 TEMU 后台数据」同步。")}</p>
            ) : (
              <KvTable rows={basicRows} />
            )}
          </section>

          <section className="tpo-return-detail__section">
            <h2 className="tpo-return-detail__section-title">{fixedUi("售卖站点")}</h2>
            <ShopSiteListPanel
              loading={siteListLoading}
              error={siteListError}
              siteList={siteList}
              shopType={viewShop?.shopType}
            />
          </section>

          <section className="tpo-return-detail__section">
            <h2 className="tpo-return-detail__section-title">{fixedUi("品牌")}</h2>
            <ShopBrandIpPanel
              loading={brandIpLoading}
              error={brandIpError}
              brands={brands}
            />
          </section>

          <section className="tpo-return-detail__section tpo-shop-info__section--categories">
            <h2 className="tpo-return-detail__section-title">{fixedUi("可发类目")}</h2>
            <ShopCategoryPanel
              loading={categoryLoading}
              error={categoryError}
              categories={categories}
              shopId={viewShopId}
            />
          </section>
        </div>
      </div>
    </>
  );
}
